import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
	AlarisBalanceService,
	AlarisButtonComponent,
	AlarisCostComponent,
	AlarisDialogService,
	AlarisDropdownActionsComponent,
	AlarisDropdownComponent,
	AlarisDropdownDirective,
	AlarisEditPanelService,
	AlarisIconDirective,
	AlarisLinkComponent,
	AlarisPBACDirective,
	AlarisProfileService,
	AlarisProgressLineComponent,
	AlarisTabsButtonComponent,
	AlarisTagComponent,
	AlarisTooltipDirective,
	ChannelUtilsService,
	EditPanelInputData,
	formatBytes,
	RowActionSimple,
	TabButtonEntity,
} from '@campaign-portal/components-library';
import { CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { exist } from '@campaign-portal/namespace/common/id';
import {
	CampaignRecurrenceType,
	ChannelType,
	PreModerationDeclineType,
	PreModerationEventStatus,
} from '@campaign-portal/namespace/common/enums';
import { CampaignEntity, CP_PERMISSIONS } from '@shared/types';
import { CampaignsService } from '../../services/campaigns.service';
import { CampaignUtilsService } from '@shared/services/campaign-utils.service';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { ContactGroupPipe } from '@shared/repo';
import { PhoneMessageComponent } from '../../shared/phone-message/phone-message.component';
import { MessageTemplatesService, SendersService, UrlShortenerUtilsService } from '@shared/services';
import { CampaignPlainContactsComponent } from './campaign-plain-contacts/campaign-plain-contacts.component';
import { CampaignDetailsChartComponent } from './campaign-details-chart/campaign-details-chart.component';
import { CampaignCascadeTableComponent } from './campaign-cascade-table/campaign-cascade-table.component';
import { CampaignDetailsActionsService } from './campaign-details.actions.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignDetailsDatesComponent } from './campaign-details-dates/campaign-details-dates.component';
import { CampaignsDialogComponent, CampaignsDialogType } from '../../shared/dialog/campaigns-dialog.component';

export interface CampaignDetailsInputData {
	campaign: CampaignEntity<exist>;
	isCampaignFromRecurrentHistory?: boolean;
}

@Component({
	selector: 'app-campaign-details',
	templateUrl: './campaign-details.component.html',
	styleUrls: ['./campaign-details.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CampaignDetailsActionsService],
	imports: [
		TranslateModule,
		AlarisPBACDirective,
		AlarisButtonComponent,
		AlarisIconDirective,
		AlarisDropdownComponent,
		AlarisDropdownActionsComponent,
		NgTemplateOutlet,
		AlarisTagComponent,
		AlarisProgressLineComponent,
		ContactGroupPipe,
		AlarisDropdownDirective,
		AlarisTooltipDirective,
		AlarisTabsButtonComponent,
		PhoneMessageComponent,
		DecimalPipe,
		AlarisLinkComponent,
		CampaignPlainContactsComponent,
		CampaignDetailsChartComponent,
		AlarisCostComponent,
		CampaignCascadeTableComponent,
		CampaignDetailsDatesComponent,
	],
	standalone: true,
})
export class CampaignDetailsComponent {
	campaign: CampaignEntity<exist>;
	readonly ChannelType = ChannelType;
	isCampaignFromRecurrentHistory = false;
	readonly CampaignStatus = CampaignStatus;
	readonly CP_PERMISSIONS = CP_PERMISSIONS;
	readonly PreModerationDeclineType = PreModerationDeclineType;
	isOpened = false;

	readonly hasShortUrlSection: boolean;
	readonly hasPreModeratedCampaignChannels: boolean;
	readonly position: ConnectedPosition[] = [
		{
			originX: 'end',
			originY: 'bottom',
			overlayX: 'end',
			overlayY: 'top',
			offsetY: 8,
		},
	];

	tabs: TabButtonEntity<ChannelType>[] = [];
	readonly tabsControl = new FormControl<ChannelType>(ChannelType.SMS, { nonNullable: true });
	readonly actions: RowActionSimple<CampaignEntity<exist>>[] = [
		this.actionsService.start,
		this.actionsService.resume,
		this.actionsService.pause,
		this.actionsService.cancel,
		this.actionsService.repeat,
		{
			...this.actionsService.deleteActionConfig,
			action: (campaign): void => {
				if (!campaign) {
					return;
				}
				this.openDialog('Delete', campaign);
			},
		},
	];
	protected readonly formatBytes = formatBytes;
	protected readonly CampaignRecurrenceType = CampaignRecurrenceType;
	private readonly destroyRef = inject(DestroyRef);

	constructor(
		public readonly bs: AlarisBalanceService,
		public readonly cu: ChannelUtilsService,
		public readonly campaignUtils: CampaignUtilsService,
		public readonly shortenerUtils: UrlShortenerUtilsService,
		@Inject(EditPanelInputData) private readonly inputData: CampaignDetailsInputData,
		private readonly actionsService: CampaignDetailsActionsService,
		private readonly profile: AlarisProfileService,
		private readonly editPanel: AlarisEditPanelService,
		private readonly dialog: AlarisDialogService,
		private readonly router: Router,
		private readonly campaignsService: CampaignsService,
		private readonly templatesService: MessageTemplatesService,
		private readonly sendersService: SendersService
	) {
		this.campaign = this.inputData.campaign;
		this.isCampaignFromRecurrentHistory = !!this.inputData.isCampaignFromRecurrentHistory;
		this.initializeTabs();
		this.hasShortUrlSection = this.checkShortUrlSection();
		this.hasPreModeratedCampaignChannels = this.checkPreModeratedCampaignChannels();

		this.actionsService.actionCompleted$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.editPanel.close());

		this.actionsService.repeatCompleted$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.editPanel.close();
			this.router.navigate(['campaigns', 'campaign-wizard']);
		});
	}

	get senderNames(): string {
		return this.campaign.setup.channels
			.map((channelSetting) => this.sendersService.map.get(channelSetting.sender)?.name)
			.join(', ');
	}

	export(id: string, fileName?: string): void {
		this.campaignsService.export(id, fileName).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	action(campaign: CampaignEntity<exist>): void {
		if (campaign.canStart()) {
			this.actionsService.start.action(campaign);
		} else if (campaign.canResume()) {
			this.actionsService.resume.action(campaign);
		} else if (campaign.canPause()) {
			this.actionsService.pause.action(campaign);
		} else if (campaign.isWaitingForApproval()) {
			this.actionsService.cancel.action(campaign);
		} else if (campaign.isTerminal()) {
			if (campaign.isFromFile()) {
				this.openDialog('Delete', campaign);
			} else {
				this.actionsService.repeat.action(campaign);
			}
		}
	}

	navigateToShortener(): void {
		const allowed = this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E]);
		const shortLink = this.campaign.info.shortLink;
		if (!shortLink || !allowed) {
			return;
		}
		this.shortenerUtils.navigateToShortener(shortLink);
		this.editPanel.close();
	}

	private openDialog(type: CampaignsDialogType, campaign: CampaignEntity): void {
		this.dialog
			.open<boolean>(CampaignsDialogComponent, {
				data: { campaign, type },
				autoFocus: false,
			})
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.editPanel.close());
	}

	private initializeTabs(): void {
		this.tabs = this.campaign.setup.channels.map((settings) => ({
			label: this.cu.name(settings.channelType),
			value: settings.channelType,
		}));
		this.tabsControl.patchValue(this.tabs[0]?.value ?? ChannelType.SMS);
	}

	private checkPreModeratedCampaignChannels(): boolean {
		return this.campaign.setup.channels.some((settings) => {
			const isPreModerated = this.campaignUtils.isPreModeratedChannel(settings.channelType);
			const templateId = settings.message?.templateID;
			const isConfirmedTemplateUsed = Boolean(
				templateId && this.templatesService.map.get(templateId)?.status === PreModerationEventStatus.CONFIRMED
			);
			return isPreModerated && !isConfirmedTemplateUsed;
		});
	}

	private checkShortUrlSection(): boolean {
		const hasShortUrl = (this.campaign.setup?.channels ?? []).some((settings) => {
			const message = this.campaignUtils.message(this.campaign, settings.channelType);
			return this.shortenerUtils.extractShortLink(message);
		});

		return Boolean(
			this.campaign.info.shortLink ||
			(this.profile.allowed([CP_PERMISSIONS.URL_SHORTENER_E]) && this.campaign.isScheduled() && hasShortUrl)
		);
	}
}
